// src/components/dashboard/candidate/profile/journey-steps/DateStep.tsx
import React from 'react';
import { Calendar } from 'lucide-react';
import { CandidateProfileCreatePayload } from '@/lib/models/candidateProfile';

interface DateStepProps {
    formData: Partial<CandidateProfileCreatePayload>;
    updateFormData: (updates: Partial<CandidateProfileCreatePayload>) => void;
}

export const DateStep: React.FC<DateStepProps> = ({ formData, updateFormData }) => {
    // Get today's date in YYYY-MM-DD format
    const today = new Date().toISOString().split('T')[0];

    // Quick options
    const quickOptions = [
        { label: 'Så snart som möjligt', days: 0 },
        { label: 'Om 2 veckor', days: 14 },
        { label: 'Om 1 månad', days: 30 },
        { label: 'Om 2 månader', days: 60 }
    ];

    const handleQuickSelect = (days: number) => {
        const date = new Date();
        date.setDate(date.getDate() + days);
        updateFormData({ available_from_date: date.toISOString().split('T')[0] });
    };

    const formatDateDisplay = (dateString: string) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString('sv-SE', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    return (
        <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-500">
            {/* Header */}
            <div className="text-center mb-8">
                <div className="w-16 h-16 mx-auto mb-4 bg-[#A4C3B2] rounded-2xl flex items-center justify-center">
                    <Calendar className="w-8 h-8 text-[#1A4D4F]" />
                </div>
                <h2 className="text-2xl font-bold mb-2">När kan du börja?</h2>
                <p className="text-gray-600">
                    Arbetsgivare planerar veckor framåt. Ju snabbare,<br />
                    desto fler möjligheter matchar du med!
                </p>
            </div>

            {/* Selected date display */}
            {formData.available_from_date && (
                <div className="text-center p-6 bg-[#A4C3B2]/30 rounded-2xl border-2 border-[#A4C3B2]">
                    <div className="text-sm text-[#1A4D4F] font-medium mb-2">Tillgänglig från</div>
                    <div className="text-2xl font-bold text-[#212121]">
                        {formatDateDisplay(formData.available_from_date)}
                    </div>
                </div>
            )}

            {/* Quick options */}
            <div>
                <label className="block text-sm font-semibold mb-3">Snabbval</label>
                <div className="grid grid-cols-2 gap-3">
                    {quickOptions.map((option) => (
                        <button
                            key={option.label}
                            type="button"
                            onClick={() => handleQuickSelect(option.days)}
                            className="p-4 border-2 border-gray-200 rounded-xl hover:border-[#1A4D4F] hover:bg-[#A4C3B2]/30 transition-all text-left font-medium"
                        >
                            {option.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* Date picker */}
            <div>
                <label className="block text-sm font-semibold mb-2">
                    Eller välj ett specifikt datum
                </label>
                <input
                    type="date"
                    value={formData.available_from_date || ''}
                    min={today}
                    onChange={(e) => updateFormData({ available_from_date: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#1A4D4F] focus:border-transparent transition-all"
                />
            </div>
        </div>
    );
};